import React, { useCallback, useEffect, useState } from 'react';

import PagePanel from 'src/components/PagePanel';
import { Select } from 'src/components/Select';
import {
  DashboardDuration,
  DURATION_OPTIONS,
  fetchBankAccountsDashboard,
  fetchDepositDashboard,
  fetchKycDashboard,
  fetchOrdersDashboard,
  fetchTransactionsDashboard,
  fetchUsersDashboard,
  fetchWithdrawDashboard,
  formatDashboardNumber,
  formatPeriodLabel,
  periodValue,
  sortPeriodCounts,
  StatusCount,
  PeriodCount,
} from 'src/api/adminDashboard';

import DashboardChartCard, { ChartPoint } from './DashboardChartCard';
import { ChartsGrid, DashboardHeader, DashboardTitle, DurationControl } from './styled';

type CardState = {
  mainValue: string;
  subtitle?: string;
  points: ChartPoint[];
  variant: 'line' | 'bar';
  loading: boolean;
  error: string | null;
};

const emptyCard = (variant: 'line' | 'bar' = 'line'): CardState => ({
  mainValue: '—',
  subtitle: undefined,
  points: [],
  variant,
  loading: true,
  error: null,
});

const statusPoints = (items?: StatusCount[]): ChartPoint[] =>
  (items || []).map((item) => ({
    label: String(item._id || '')
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase()),
    value: Number(item.count) || 0,
  }));

const seriesPoints = (items: PeriodCount[], duration: DashboardDuration): ChartPoint[] =>
  sortPeriodCounts(items, duration).map((item) => ({
    label: formatPeriodLabel(String(item._id)),
    value: periodValue(item.count),
  }));

const errMessage = (err: unknown) => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 403) return 'No permission';
  if (status === 401) return 'Unauthorized';
  return 'Failed to load';
};

const Dashboard = () => {
  const [duration, setDuration] = useState<DashboardDuration>('monthly');
  const durationLabel =
    DURATION_OPTIONS.find((o) => o.value === duration)?.label ?? 'Monthly';

  const [deposits, setDeposits] = useState<CardState>(emptyCard('line'));
  const [withdrawals, setWithdrawals] = useState<CardState>(emptyCard('line'));
  const [users, setUsers] = useState<CardState>(emptyCard('bar'));
  const [orders, setOrders] = useState<CardState>(emptyCard('bar'));
  const [bankAccounts, setBankAccounts] = useState<CardState>(emptyCard('line'));
  const [kyc, setKyc] = useState<CardState>(emptyCard('line'));
  const [transactions, setTransactions] = useState<CardState>(emptyCard('line'));

  const load = useCallback(async () => {
    setDeposits(emptyCard('line'));
    setWithdrawals(emptyCard('line'));
    setUsers(emptyCard('bar'));
    setOrders(emptyCard('bar'));
    setBankAccounts(emptyCard('line'));
    setKyc(emptyCard('line'));
    setTransactions(emptyCard('line'));

    const settle = async <T,>(
      promise: Promise<T>,
      // eslint-disable-next-line no-unused-vars
      onOk: (payload: T) => CardState,
      setState: React.Dispatch<React.SetStateAction<CardState>>,
      variant: 'line' | 'bar',
    ) => {
      try {
        const data = await promise;
        setState({ ...onOk(data), loading: false, error: null });
      } catch (err) {
        setState({ ...emptyCard(variant), loading: false, error: errMessage(err) });
      }
    };

    await Promise.all([
      settle(
        fetchDepositDashboard(duration),
        (data) => ({
          mainValue: formatDashboardNumber(data.total_count),
          subtitle: `${formatDashboardNumber(data.total_count)} Deposits`,
          points: statusPoints(data.deposit_transaction_count),
          variant: 'line' as const,
          loading: false,
          error: null,
        }),
        setDeposits,
        'line',
      ),
      settle(
        fetchWithdrawDashboard(duration),
        (data) => ({
          mainValue: formatDashboardNumber(data.total_count),
          subtitle: `${formatDashboardNumber(data.total_count)} Withdrawals`,
          points: statusPoints(data.withdraw_transaction_count),
          variant: 'line' as const,
          loading: false,
          error: null,
        }),
        setWithdrawals,
        'line',
      ),
      settle(
        fetchUsersDashboard(duration),
        (data) => ({
          mainValue: formatDashboardNumber(data.total_count),
          subtitle: undefined,
          points: seriesPoints(data.user_count || [], duration),
          variant: 'bar' as const,
          loading: false,
          error: null,
        }),
        setUsers,
        'bar',
      ),
      settle(
        fetchOrdersDashboard(duration),
        (data) => ({
          mainValue: formatDashboardNumber(data.total_count),
          subtitle: undefined,
          points: statusPoints(data.orders_count),
          variant: 'bar' as const,
          loading: false,
          error: null,
        }),
        setOrders,
        'bar',
      ),
      settle(
        fetchBankAccountsDashboard(duration),
        (data) => ({
          mainValue: formatDashboardNumber(data.total_count),
          subtitle: `${formatDashboardNumber(data.total_count)} Accounts`,
          points: statusPoints(data.bank_accounts_count),
          variant: 'line' as const,
          loading: false,
          error: null,
        }),
        setBankAccounts,
        'line',
      ),
      settle(
        fetchKycDashboard(duration),
        (data) => ({
          mainValue: formatDashboardNumber(data.total_count),
          subtitle: undefined,
          points: seriesPoints(data.kyc_count || [], duration),
          variant: 'line' as const,
          loading: false,
          error: null,
        }),
        setKyc,
        'line',
      ),
      settle(
        fetchTransactionsDashboard(duration),
        (data) => {
          const series = seriesPoints(data.transaction_count || [], duration);
          const total = series.reduce((sum, p) => sum + p.value, 0);
          return {
            mainValue: formatDashboardNumber(total),
            subtitle: `${formatDashboardNumber(total)} Transactions`,
            points: series,
            variant: 'line' as const,
            loading: false,
            error: null,
          };
        },
        setTransactions,
        'line',
      ),
    ]);
  }, [duration]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PagePanel>
      <DashboardHeader>
        <DashboardTitle>Dashboard</DashboardTitle>
        <DurationControl>
          <Select
            compact
            options={DURATION_OPTIONS}
            value={duration}
            onChange={(e) => setDuration(e.target.value as DashboardDuration)}
          />
        </DurationControl>
      </DashboardHeader>

      <ChartsGrid>
        <DashboardChartCard
          title="Deposits"
          durationLabel={durationLabel}
          mainValue={deposits.mainValue}
          subtitle={deposits.subtitle}
          points={deposits.points}
          variant="line"
          loading={deposits.loading}
          error={deposits.error}
        />
        <DashboardChartCard
          title="Withdrawals"
          durationLabel={durationLabel}
          mainValue={withdrawals.mainValue}
          subtitle={withdrawals.subtitle}
          points={withdrawals.points}
          variant="line"
          loading={withdrawals.loading}
          error={withdrawals.error}
        />
        <DashboardChartCard
          title="Users"
          durationLabel={durationLabel}
          mainValue={users.mainValue}
          subtitle={users.subtitle}
          points={users.points}
          variant="bar"
          loading={users.loading}
          error={users.error}
        />
        <DashboardChartCard
          title="Orders"
          durationLabel={durationLabel}
          mainValue={orders.mainValue}
          subtitle={orders.subtitle}
          points={orders.points}
          variant="bar"
          loading={orders.loading}
          error={orders.error}
        />
        <DashboardChartCard
          title="Bank Accounts"
          durationLabel={durationLabel}
          mainValue={bankAccounts.mainValue}
          subtitle={bankAccounts.subtitle}
          points={bankAccounts.points}
          variant="line"
          loading={bankAccounts.loading}
          error={bankAccounts.error}
        />
        <DashboardChartCard
          title="KYC"
          durationLabel={durationLabel}
          mainValue={kyc.mainValue}
          subtitle={kyc.subtitle}
          points={kyc.points}
          variant="line"
          loading={kyc.loading}
          error={kyc.error}
        />
        <DashboardChartCard
          title="Transactions"
          durationLabel={durationLabel}
          mainValue={transactions.mainValue}
          subtitle={transactions.subtitle}
          points={transactions.points}
          variant="line"
          loading={transactions.loading}
          error={transactions.error}
        />
      </ChartsGrid>
    </PagePanel>
  );
};

export default Dashboard;
